import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument } from '../firebase';
import { getUserName, isSameUser } from '../utils/userUtils';
import LoadingChip from './LoadingChip';
import { DAYS, dateToTripDay, parseTimeAndDate } from '../utils/dateUtils';
import { CalendarDays } from 'lucide-react';

const EMOJIS = ["☀️", "🌹", "🍽️", "🌊", "🌴", "🧘", "🚗", "🍷", "🍳", "🏡", "🎒", "🥩", "🥞", "🛒", "🔥", "🚲", "🍿", "🏓"];

const formatPassengersList = (names) => {
  if (!names || names.length === 0) return "Guest";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
};

export default function Planning({ users = [] }) {
  // --- Schedule States ---
  const [scheduleItems, setScheduleItems] = useState([]);
  const [activities, setActivities] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [legs, setLegs] = useState([]);
  const [dinners, setDinners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeDay, setActiveDay] = useState("Thursday 1/7");
  const [showAddForm, setShowAddForm] = useState(false);
  const [formMode, setFormMode] = useState("freeform");

  // Schedule Event Form Fields
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [time, setTime] = useState("");
  const [title, setTitle] = useState("");
  const [emoji, setEmoji] = useState("📅");
  const [notes, setNotes] = useState("");
  const [day, setDay] = useState(activeDay);

  // Schedule Event Edit States
  const [editingItemId, setEditingItemId] = useState(null);
  const [editDay, setEditDay] = useState("Thursday 1/7");
  const [editTime, setEditTime] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editEmoji, setEditEmoji] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const loggedInUserId = localStorage.getItem("dayones_user_name") || "";

  // Subscriptions useEffect
  useEffect(() => {
    setIsLoading(true);
    // Subscribe to Schedule collection
    const unsubscribeSchedule = subscribeToCollection("schedule", (data) => {
      setScheduleItems(data);
      setIsLoading(false);
    });

    // Subscribe to Activities to allow linking them
    const unsubscribeActivities = subscribeToCollection("activities", (data) => {
      setActivities(data);
    });

    // Subscribe to Flight itineraries
    const unsubscribeItineraries = subscribeToCollection("itineraries_v3", (data) => {
      setItineraries(data);
    });

    // Subscribe to Flight legs
    const unsubscribeLegs = subscribeToCollection("legs_v3", (data) => {
      setLegs(data);
    });

    // Subscribe to Dinners
    const unsubscribeDinners = subscribeToCollection("dinners", (data) => {
      setDinners(data);
    });

    return () => {
      unsubscribeSchedule();
      unsubscribeActivities();
      unsubscribeItineraries();
      unsubscribeLegs();
      unsubscribeDinners();
    };
  }, []);

  // "Put on Schedule" deep-link from the Activities page: it stores the
  // activity id in sessionStorage (see LocalActivities.js) rather than a
  // hash query string, because App.js's own hash<->tab sync effect rewrites
  // location.hash right after navigation and would strip a query string
  // before this ever got to read it.
  useEffect(() => {
    const checkPrefillActivity = () => {
      const actId = sessionStorage.getItem('dayones_prefill_activity');
      if (actId) {
        sessionStorage.removeItem('dayones_prefill_activity');
        setSelectedActivityId(actId);
        setFormMode("activity");
        setShowAddForm(true);

        // Find the activity if loaded, and pre-populate title & emoji
        const act = activities.find(a => a.id === actId);
        if (act) {
          setTitle(act.title || "");
          setEmoji(act.emoji || "☀️");
        }
      }
    };

    if (activities.length > 0) {
      checkPrefillActivity();
    }
  }, [activities]);

  // When selected activity changes, auto-fill title & emoji only (notes are sourced from the live activity)
  const handleSelectActivity = (activityId) => {
    setSelectedActivityId(activityId);
    if (!activityId) {
      setTitle("");
      setEmoji("☀️");
      return;
    }
    const act = activities.find(a => a.id === activityId);
    if (act) {
      setTitle(act.title || "");
      setEmoji(act.emoji || "☀️");
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    const isLinkedActivity = formMode === "activity";
    if (!isLinkedActivity && !title.trim()) {
      alert("Event Title is required!");
      return;
    }
    if (isLinkedActivity && !selectedActivityId) {
      alert("Please choose a Nominated Activity to link.");
      return;
    }
    if (!time.trim()) {
      alert("Time is required!");
      return;
    }

    const newItem = {
      id: `sched_${Date.now()}`,
      day,
      time: time.trim(),
      title: isLinkedActivity ? "" : title.trim(),
      emoji: isLinkedActivity ? "" : emoji.trim(),
      notes: "",
      activityId: isLinkedActivity ? selectedActivityId : "",
      createdBy: loggedInUserId
    };

    if (!isLinkedActivity) {
      newItem.notes = notes.trim();
    }

    await saveDocument("schedule", newItem.id, newItem);

    // Reset Form
    setTitle("");
    setTime("");
    setNotes("");
    setEmoji("📅");
    setSelectedActivityId("");
    setShowAddForm(false);
  };

  const handleStartEdit = (item) => {
    setEditingItemId(item.id);
    setEditDay(item.day || "Thursday 1/7");
    setEditTime(item.time || "");
    setEditTitle(item.title || "");
    setEditEmoji(item.emoji || "📅");
    setEditNotes(item.notes || "");
  };

  const handleSaveEdit = async (item) => {
    const isLinkedActivity = !!item.activityId;
    if (!isLinkedActivity && !editTitle.trim()) {
      alert("Title is required!");
      return;
    }
    if (!editTime.trim()) {
      alert("Time is required!");
      return;
    }

    const updated = {
      ...item,
      day: editDay,
      time: editTime.trim(),
      ...(isLinkedActivity ? {} : {
        title: editTitle.trim(),
        emoji: editEmoji.trim(),
        notes: editNotes.trim()
      })
    };

    await saveDocument("schedule", item.id, updated);
    setEditingItemId(null);
  };

  const handleDeleteEvent = async (itemId) => {
    if (!window.confirm("Are you sure you want to remove this event from the schedule?")) return;
    await deleteDocument("schedule", itemId);
  };

  // Cron/Chronological sorting helper scores
  const timeToSortScore = (timeStr) => {
    if (!timeStr) return 9999;
    const normalized = timeStr.trim().toLowerCase();

    if (normalized.includes("all day") || normalized.includes("morning")) return 0;
    if (normalized.includes("noon")) return 12 * 60;
    if (normalized.includes("afternoon")) return 13 * 60;
    if (normalized.includes("evening") || normalized.includes("sunset")) return 18 * 60;
    if (normalized.includes("night") || normalized.includes("dinner")) return 20 * 60;

    const match = normalized.match(/(\d+)(?::(\d+))?\s*(am|pm)?/);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2] ? parseInt(match[2], 10) : 0;
      const ampm = match[3];

      if (ampm === "pm" && hours < 12) hours += 12;
      if (ampm === "am" && hours === 12) hours = 0;

      return hours * 60 + minutes;
    }
    return 9999;
  };

  // Dynamically map flights to schedule items
  const flightScheduleItems = [];
  itineraries.forEach(itinerary => {
    const itineraryLegs = legs.filter(l => l.itineraryId === itinerary.id);
    if (itineraryLegs.length === 0) return;

    const getLegDepartureTimeMs = (leg) => {
      const depVal = leg.departure?.scheduled || leg.departure?.estimated || leg.date || "";
      if (!depVal) return 0;
      const parsed = Date.parse(depVal);
      return isNaN(parsed) ? 0 : parsed;
    };

    const sortedLegs = [...itineraryLegs].sort((a, b) => getLegDepartureTimeMs(a) - getLegDepartureTimeMs(b));
    const isIncoming = itinerary.direction === "incoming";
    const targetLeg = isIncoming ? sortedLegs[sortedLegs.length - 1] : sortedLegs[0];

    const rawDepTime = targetLeg.departure?.scheduled || targetLeg.departure?.estimated || "";
    const rawArrTime = targetLeg.arrival?.scheduled || targetLeg.arrival?.estimated || "";

    const depParsed = parseTimeAndDate(rawDepTime || targetLeg.date);
    const arrParsed = parseTimeAndDate(rawArrTime);

    const depTime = depParsed.time;
    const arrTime = arrParsed.time;

    const depDate = depParsed.date || targetLeg.date;
    const arrDate = arrParsed.date || targetLeg.date;

    const targetDate = isIncoming ? arrDate : depDate;
    const tripDay = dateToTripDay(targetDate);

    if (!tripDay) return;

    const passengerNames = Array.isArray(itinerary.passengers)
      ? itinerary.passengers.map(p => getUserName(users, p))
      : [];

    const depAirport = targetLeg.departureAirport || targetLeg.departure?.iata || "TBD";
    const arrAirport = targetLeg.arrival?.iata || targetLeg.arrival?.airport || "TBD";
    const flightNum = `${targetLeg.airlineCode}${targetLeg.flightNumber}`;

    const passengersStr = formatPassengersList(passengerNames);

    const flightStatusLabel = targetLeg.flight_status
      ? targetLeg.flight_status.charAt(0).toUpperCase() + targetLeg.flight_status.slice(1).toLowerCase()
      : "Scheduled";

    if (isIncoming) {
      flightScheduleItems.push({
        id: `flight_arr_${targetLeg.id}`,
        day: tripDay,
        time: arrTime || "TBD",
        title: `Arrival: ${passengersStr} (${arrAirport})`,
        emoji: "🛬",
        notes: `Flight ${flightNum} from ${depAirport} (${flightStatusLabel})`,
        createdBy: "System",
        isFlight: true,
        isArrival: true,
        flightyUrl: targetLeg.flightyUrl,
        itineraryId: itinerary.id
      });
    } else {
      flightScheduleItems.push({
        id: `flight_dep_${targetLeg.id}`,
        day: tripDay,
        time: depTime || "TBD",
        title: `Departure: ${passengersStr} (${depAirport})`,
        emoji: "🛫",
        notes: `Flight ${flightNum} to ${arrAirport} (${flightStatusLabel})`,
        createdBy: "System",
        isFlight: true,
        isArrival: false,
        flightyUrl: targetLeg.flightyUrl,
        itineraryId: itinerary.id
      });
    }
  });

  // Dinner virtual schedule items
  const DINNER_DAYS = [
    "Thursday 1/7", "Friday 1/8", "Saturday 1/9", "Sunday 1/10",
    "Monday 1/11", "Tuesday 1/12", "Wednesday 1/13"
  ];
  const dinnerScheduleItems = DINNER_DAYS.map(dinnerDay => {
    const dinnerDoc = dinners.find(d => d.day === dinnerDay);
    return {
      id: `dinner_${dinnerDay.replace(/[^a-z0-9]/gi, '_')}`,
      day: dinnerDay,
      time: "Dinner",
      title: (dinnerDoc && dinnerDoc.theme) ? dinnerDoc.theme : "Dinner (TBD)",
      emoji: "🍽️",
      notes: (dinnerDoc && dinnerDoc.menu) ? dinnerDoc.menu : "",
      createdBy: "System",
      isDinner: true,
      dinnerFirestoreId: dinnerDoc ? dinnerDoc.id : "",
      dinnerType: (dinnerDoc && dinnerDoc.type === "restaurant") ? "restaurant" : "cooking",
      chef: (dinnerDoc && dinnerDoc.chef) ? dinnerDoc.chef : "",
      washer: (dinnerDoc && dinnerDoc.washer) ? dinnerDoc.washer : "",
      attendees: (dinnerDoc && dinnerDoc.attendees) ? dinnerDoc.attendees : ""
    };
  });

  // Filter & sort active day items
  const userItems = scheduleItems.filter(item => item.day === activeDay);
  const flightItems = flightScheduleItems.filter(item => item.day === activeDay);
  const dinnerItems = dinnerScheduleItems.filter(item => item.day === activeDay);

  const allDayItems = [...userItems, ...flightItems, ...dinnerItems].sort((a, b) => timeToSortScore(a.time) - timeToSortScore(b.time));

  // Count items per day for the visual badges
  const getCountForDay = (dayName) => {
    const userCount = scheduleItems.filter(item => item.day === dayName).length;
    const flightCount = flightScheduleItems.filter(item => item.day === dayName).length;
    const dinnerCount = dinnerScheduleItems.filter(item => item.day === dayName).length;
    return userCount + flightCount + dinnerCount;
  };

  return (
    <div className="tab-panel">
      {/* Normalized styles and schedule elements */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .schedule-timeline-container {
          position: relative;
          padding-left: 32px;
          margin-top: 24px;
        }
        .schedule-timeline-line {
          position: absolute;
          left: 11px;
          top: 8px;
          bottom: 8px;
          width: 2px;
          background: linear-gradient(to bottom, var(--color-primary), var(--color-secondary), rgba(252, 249, 245, 0.05));
        }
        /* .schedule-day-tabs-wrapper / .schedule-day-tab / .schedule-badge
           deliberately have NO local styling here anymore — this used to
           duplicate (with different, drifting values) the shared rules in
           App.css/index.css that FlightTrackerV3's identical day tabs rely
           on, which is exactly why Itinerary's day tabs (background
           opacity, hover behavior, and the active tab's border color) kept
           diverging from the Flights tab's. Itinerary now uses the same
           global .schedule-day-tab rules Flights already does, so the two
           stay in sync by construction instead of by manually mirroring
           edits between two places. */
        .timeline-item {
          position: relative;
          margin-bottom: 28px;
        }
        .timeline-marker {
          position: absolute;
          left: -32px;
          top: 6px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--color-bg-dark, #1a1a1a);
          border: 3px solid var(--color-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          z-index: 2;
          box-shadow: 0 0 8px rgba(var(--color-primary-rgb), 0.4);
          transition: all 0.3s ease;
        }
        .timeline-item:hover .timeline-marker {
          border-color: var(--color-secondary);
          box-shadow: 0 0 10px rgba(198, 138, 53, 0.5);
          transform: scale(1.1);
        }
        .timeline-card {
          margin-left: 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .timeline-card:hover {
          transform: translateX(4px);
        }
      `}} />

      <div className="view-title-container" style={{ marginBottom: '20px' }}>
        <div className="view-title">
          <h2>
            <CalendarDays className="subpage-header-icon" strokeWidth={1.75} />
            Itinerary
          </h2>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px' }}>
        <button className="btn-primary" onClick={() => { if (!showAddForm) setDay(activeDay); setShowAddForm(!showAddForm); }} style={{ padding: '6px 14px', fontSize: '0.85rem' }}>
          {showAddForm ? "Cancel Event" : "+ Add Event"}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddEvent} className="glass-panel" style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.25rem', color: 'var(--color-text-main)', margin: 0 }}>Schedule an Event</h3>

          <div style={{ display: 'flex', gap: '16px', borderBottom: '1px solid var(--color-glass-border)', paddingBottom: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', cursor: 'pointer', fontWeight: '600' }}>
              <input
                type="radio"
                name="formMode"
                checked={formMode === "freeform"}
                onChange={() => { setFormMode("freeform"); setSelectedActivityId(""); setTitle(""); }}
              />
              Freeform Event
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', cursor: 'pointer', fontWeight: '600' }}>
              <input
                type="radio"
                name="formMode"
                checked={formMode === "activity"}
                onChange={() => setFormMode("activity")}
              />
              Link Nominated Activity
            </label>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Day of Trip</label>
              <select className="input-field" value={day} onChange={e => setDay(e.target.value)} required>
                {DAYS.map(d => (
                  <option key={d} value={d} style={{ background: '#242220' }}>{d}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Time / Window (e.g. 10:00 AM, Sunset)</label>
              <input
                type="text"
                placeholder="e.g. 10:00 AM or All Day"
                className="input-field"
                value={time}
                onChange={e => setTime(e.target.value)}
                required
              />
            </div>

            {formMode === "activity" ? (
              <div className="form-group">
                <label>Choose Live Activity</label>
                <select
                  className="input-field"
                  value={selectedActivityId}
                  onChange={e => handleSelectActivity(e.target.value)}
                  required
                >
                  <option value="" style={{ background: '#242220', color: '#8a8272' }}>-- Choose Activity --</option>
                  {activities.map(a => (
                    <option key={a.id} value={a.id} style={{ background: '#242220' }}>
                      {a.emoji || "☀️"} {a.title} (❤️ {a.votes || 0})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-group" style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label>Event Title</label>
                  <input
                    type="text"
                    placeholder="e.g. Morning Family Walk"
                    className="input-field"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    required
                  />
                </div>
                <div style={{ width: '80px' }}>
                  <label>Emoji</label>
                  <select
                    className="input-field"
                    value={emoji}
                    onChange={e => setEmoji(e.target.value)}
                  >
                    {EMOJIS.map(em => (
                      <option key={em} value={em} style={{ background: '#242220' }}>{em}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {formMode !== "activity" && (
            <div className="form-group">
              <label>Scheduling Notes / Directives (e.g. Who is driving, what to bring)</label>
              <textarea
                placeholder="e.g. Pack swimming gears and water bottles. Meeting in the lodge lobby."
                className="input-field"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows="3"
              />
            </div>
          )}

          <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>Schedule Event</button>
        </form>
      )}

      {/* Day Swapping Navigation Bar */}
      <div className="schedule-day-tabs-wrapper">
        {DAYS.map(d => {
          const count = getCountForDay(d);
          return (
            <div
              key={d}
              className={`schedule-day-tab ${activeDay === d ? 'active' : ''}`}
              onClick={() => setActiveDay(d)}
            >
              <span>{d}</span>
              {count > 0 && <span className="schedule-badge">{count}</span>}
            </div>
          );
        })}
      </div>

      {isLoading ? (
        <LoadingChip text="Syncing Schedule Timeline..." />
      ) : allDayItems.length === 0 ? (
        <div className="glass-panel" style={{ padding: '48px', textAlign: 'center', color: 'var(--color-text-muted)', width: '100%' }}>
          No events scheduled for {activeDay} yet. Add an event to the day's itinerary!
        </div>
      ) : (
        <div className="schedule-timeline-container">
          <div className="schedule-timeline-line"></div>

          {allDayItems.map((item, index) => {
            const isEditing = editingItemId === item.id;
            const isOwner = isSameUser(users, item.createdBy, loggedInUserId);
            const linkedActivity = item.activityId ? activities.find(a => a.id === item.activityId) : null;

            if (isEditing) {
              const isLinkedEdit = !!item.activityId;
              return (
                <div key={item.id} className="timeline-item">
                  <div className="timeline-marker">✏️</div>
                  <div className="glass-panel timeline-card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', borderLeft: '3px solid var(--color-primary)' }}>
                    <h4 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--color-text-main)' }}>Edit Scheduled Event</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div className="form-group">
                        <label style={{ fontSize: '0.8rem' }}>Day of Trip</label>
                        <select
                          className="input-field"
                          value={editDay}
                          onChange={e => setEditDay(e.target.value)}
                          style={{ fontSize: '0.85rem', width: '100%' }}
                        >
                          {DAYS.map(d => (
                            <option key={d} value={d} style={{ background: '#242220' }}>{d}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '0.8rem' }}>Time / Window</label>
                        <input
                          type="text"
                          className="input-field"
                          value={editTime}
                          onChange={e => setEditTime(e.target.value)}
                          style={{ fontSize: '0.85rem', width: '100%' }}
                        />
                      </div>
                    </div>
                    {(() => {
                      const canEditContent = isOwner && !isLinkedEdit;
                      if (!canEditContent) return (
                        <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: 0, fontStyle: 'italic' }}>
                          {isLinkedEdit
                            ? 'Title, emoji, and notes are sourced from the linked Nominated Activity.'
                            : 'Only the event creator can edit the title and notes.'}
                        </p>
                      );
                      return (
                        <>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <div className="form-group" style={{ flex: 1, minWidth: '160px' }}>
                              <label style={{ fontSize: '0.8rem' }}>Title</label>
                              <input
                                type="text"
                                className="input-field"
                                value={editTitle}
                                onChange={e => setEditTitle(e.target.value)}
                                style={{ fontSize: '0.85rem', width: '100%' }}
                              />
                            </div>
                            <div className="form-group" style={{ width: '80px' }}>
                              <label style={{ fontSize: '0.8rem' }}>Emoji</label>
                              <input
                                type="text"
                                className="input-field"
                                value={editEmoji}
                                onChange={e => setEditEmoji(e.target.value)}
                                style={{ fontSize: '0.85rem', textAlign: 'center', width: '100%' }}
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '0.8rem' }}>Notes</label>
                            <textarea
                              className="input-field"
                              value={editNotes}
                              onChange={e => setEditNotes(e.target.value)}
                              rows="2"
                              style={{ fontSize: '0.85rem', width: '100%' }}
                            />
                          </div>
                        </>
                      );
                    })()}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <button onClick={() => handleSaveEdit(item)} className="btn-primary" style={{ padding: '6px 14px', fontSize: '0.8rem' }}>Save</button>
                      <button onClick={() => setEditingItemId(null)} className="btn-secondary" style={{ padding: '6px 14px', fontSize: '0.8rem' }}>Cancel</button>
                    </div>
                  </div>
                </div>
              );
            }

            // Normal Timeline Item View
            let displayTitle = item.title;
            let displayEmoji = item.emoji;
            let displayNotes = item.notes;

            if (linkedActivity) {
              displayTitle = linkedActivity.title;
              displayEmoji = linkedActivity.emoji || "📅";
              displayNotes = linkedActivity.description;
            }

            let cardColor = 'var(--color-primary)';
            let cardBg = 'rgba(var(--color-primary-rgb), 0.08)';
            let cardBorder = 'rgba(var(--color-primary-rgb), 0.15)';

            if (item.isFlight) {
              cardColor = 'var(--color-accent)';
              cardBg = 'rgba(30, 61, 43, 0.08)';
              cardBorder = 'rgba(30, 61, 43, 0.15)';
            } else if (item.isDinner) {
              cardColor = 'var(--color-secondary)';
              cardBg = 'rgba(198, 138, 53, 0.08)';
              cardBorder = 'rgba(198, 138, 53, 0.15)';
            }

            return (
              <div key={item.id} className="timeline-item">
                <div className="timeline-marker" style={{ fontSize: '0.8rem' }}>
                  {displayEmoji}
                </div>

                <div className="glass-panel timeline-card" style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: '6px', borderLeft: '3px solid var(--color-primary)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        color: cardColor,
                        background: cardBg,
                        padding: '2px 8px',
                        borderRadius: '0px',
                        border: `1px solid ${cardBorder}`
                      }}>
                        {item.time}
                      </span>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', fontFamily: 'var(--font-title)', fontWeight: '600' }}>
                        {displayTitle}
                      </h3>
                    </div>

                    {!item.isFlight && !item.isDinner && (
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button
                          onClick={() => handleStartEdit(item)}
                          className="btn-inline-edit"
                          title="Edit Event"
                        >
                          [Edit]
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => handleDeleteEvent(item.id)}
                            className="btn-inline-delete"
                            title="Remove Event"
                          >
                            [Remove]
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {displayNotes && (
                    <p style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-text-muted)',
                      margin: '2px 0 4px 0',
                      lineHeight: '1.5',
                      wordBreak: 'normal',
                      overflowWrap: 'break-word'
                    }}>
                      {displayNotes}
                    </p>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: '1px solid var(--color-glass-border)',
                    paddingTop: '8px',
                    marginTop: '2px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {item.isDinner ? (
                      item.dinnerType === "restaurant" ? (
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                          <span>🍽️ Going: <strong style={{ color: 'var(--color-text-main)' }}>{item.attendees ? getUserName(users, item.attendees) : 'TBD'}</strong></span>
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                          <span>👨‍🍳 Chef: <strong style={{ color: 'var(--color-text-main)' }}>{item.chef ? getUserName(users, item.chef) : 'TBD'}</strong></span>
                          <span>🧼 Clean-up: <strong style={{ color: 'var(--color-text-main)' }}>{item.washer ? getUserName(users, item.washer) : 'TBD'}</strong></span>
                        </span>
                      )
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        Added by: <strong style={{ color: 'var(--color-text-main)' }}>{item.isFlight ? "System sync" : getUserName(users, item.createdBy)}</strong>
                      </span>
                    )}

                    {linkedActivity && (
                      <a
                        href="#activities"
                        onClick={() => {
                          sessionStorage.setItem('dayones_focus_activity', item.activityId);
                        }}
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-secondary)',
                          fontWeight: '600',
                          textDecoration: 'none'
                        }}
                      >
                        View Activity Details
                      </a>
                    )}

                    {item.isDinner && (
                      <a
                        href="#dinners"
                        onClick={() => {
                          if (item.dinnerFirestoreId) sessionStorage.setItem('dayones_focus_dinner', item.dinnerFirestoreId);
                        }}
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-secondary)',
                          fontWeight: '600',
                          textDecoration: 'none'
                        }}
                      >
                        Manage Dinner Signups
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
